import { Controller, useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import {
  InputWithIcon,
  Container as InputContainer,
} from '../../../../components/Toolkit/Inputs/InputWithIcon';
import { Select } from '../../../../components/Toolkit/Inputs/Select';
import { TextField } from '../../../../components/Toolkit/Inputs/TextField';
import { Label } from '../../../../components/Toolkit/Inputs/Label';

import { media } from '../../../../helpers/breakpoints';
import {
  formatFormNumber,
  removeNonNumericCharacters,
} from '../../../../helpers/forms';
import { MILEAGE_UNIT } from '../../../../types';
import type { ValuationFormValues } from '../../../../components/CarValuationPage/components/ValuationTool/ValuationTool.schema';

const SInputWithIcon = styled(InputWithIcon)`
  margin-bottom: ${({ theme }) => theme.spacing.M16};

  ${InputContainer} {
    grid-template-columns: 1fr;
  }

  label {
    ${({ theme }) => theme.fontSize.M16};
    color: ${({ theme }) => theme.colors.GREY_DARK};
    margin-bottom: ${({ theme }) => theme.spacing.S8};
  }

  ${media.large} {
    flex: 50%;
    margin-right: ${({ theme }) => theme.spacing.M16};
    margin-bottom: 0;
  }
`;

const SOdometerField = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.M16};

  label {
    ${({ theme }) => theme.fontSize.M16};
    color: ${({ theme }) => theme.colors.GREY_DARK};
    margin-bottom: ${({ theme }) => theme.spacing.S8};
  }

  ${media.large} {
    flex: 50%;
    margin-bottom: 0;
  }
`;

const SOdometerContainer = styled.div`
  display: flex;
  height: 40px;
`;

const SMileageInput = styled(TextField)`
  flex-grow: 1;

  ${media.large} {
    width: 188px;
  }
`;

const SMileageTypeSelect = styled(Select)`
  width: ${({ theme }) => theme.spacing.XL72};
  margin-left: ${({ theme }) => theme.spacing.S8};

  ${media.large} {
    width: 94px;
  }
`;

export const Container = styled.div`
  ${media.large} {
    width: 100%;
    display: flex;
    margin-bottom: ${({ theme }) => theme.spacing.M24};
  }
`;

const mileageOptions = [
  { label: 'km', value: MILEAGE_UNIT.KILOMETRES },
  { label: 'miles', value: MILEAGE_UNIT.MILES },
];

type TValuationTool = {
  cdnUrl: string;
};

function ValuationToolFields({ cdnUrl }: TValuationTool) {
  const {
    control,
    formState: { errors },
  } = useFormContext<ValuationFormValues>();

  return (
    <Container>
      <Controller
        name="registration"
        control={control}
        render={({ field }) => (
          <SInputWithIcon
            icon={`${cdnUrl}/images/illustrations/eu-symbol-v2.svg`}
            label="Registration number"
            name={field.name}
            onChange={field.onChange}
            value={field.value}
            placeholder="e.g 191WX1234"
            willUseSubText={false}
            willUseButton={false}
            errorMessage={errors.registration?.message}
          />
        )}
      />
      <SOdometerField>
        <Label htmlFor="mileage">Odometer reading</Label>
        <SOdometerContainer>
          <Controller
            name="mileage"
            control={control}
            render={({ field }) => (
              <SMileageInput
                name={field.name}
                id="mileage"
                value={String(formatFormNumber(field.value) ?? '')}
                onChange={(value) =>
                  field.onChange(removeNonNumericCharacters(value))
                }
                type="text"
                inputMode="numeric"
                placeholder="e.g 45,000"
                hasError={Boolean(errors.mileage)}
                willUseSubText={false}
              />
            )}
          />
          <Controller
            name="mileageType"
            control={control}
            render={({ field }) => (
              <SMileageTypeSelect
                name={field.name}
                value={field.value}
                onChange={field.onChange}
                options={mileageOptions}
                hasError={Boolean(errors.mileage)}
                willUseSubText={false}
                aria-label="Mileage unit"
              />
            )}
          />
        </SOdometerContainer>
      </SOdometerField>
    </Container>
  );
}

export { ValuationToolFields };
